/**
 * Centralized case status utilities.
 */
import type { CaseStatus } from '../types';

/**
 * Statuses that indicate a case has a translation (HTML content exists)
 */
const STATUSES_WITH_TRANSLATION: CaseStatus[] = [
  'translated',
  'generating_pdf',
  'ready_for_review',
  'approved',
  'uploaded',
  'returned',
  'lost',
  'found',
  'other',
];

/**
 * Statuses that indicate a case has a generated PDF
 */
const STATUSES_WITH_PDF: CaseStatus[] = [
  'ready_for_review',
  'approved',
  'uploading',
  'uploaded',
  'returned',
  'lost',
  'found',
  'other',
];

/**
 * Statuses where the Approve button should be shown
 */
const CAN_APPROVE_STATUSES: CaseStatus[] = ['ready_for_review'];

/**
 * Statuses that indicate a case is approved for upload
 */
const APPROVED_STATUSES: CaseStatus[] = ['approved'];

/**
 * Check if a case status indicates translation content exists
 */
export const hasTranslation = (status: string | undefined | null): boolean => {
  return STATUSES_WITH_TRANSLATION.includes(status as CaseStatus);
};

/**
 * Check if a case status indicates a PDF has been generated
 */
export const hasPdf = (status: string | undefined | null): boolean => {
  return STATUSES_WITH_PDF.includes(status as CaseStatus);
};

/**
 * Check if a case status indicates it's approved
 */
export const isApproved = (status: string | undefined | null): boolean => {
  return APPROVED_STATUSES.includes(status as CaseStatus);
};

/**
 * Check if a case can be approved (only ready_for_review)
 */
export const canApprove = (status: string | undefined | null): boolean => {
  return CAN_APPROVE_STATUSES.includes(status as CaseStatus);
};

/**
 * Check if a case is in review status (can edit HTML and regenerate PDF)
 */
export const isInReview = (status: string | undefined | null): boolean => {
  return status === 'ready_for_review';
};

/**
 * Statuses that cannot be manually changed (process or uploaded)
 */
const BLOCKED_STATUS_CHANGE: CaseStatus[] = [
  'pending',
  'translating',
  'generating_pdf',
  'uploading',
  'uploaded',
];

/**
 * Statuses that can be manually set
 */
export const MANUAL_STATUS_OPTIONS: { value: CaseStatus; label: string }[] = [
  { value: 'ready_for_review', label: 'Ready for Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'other', label: 'Other' },
];

/**
 * Check if a case's status can be manually changed
 */
export const canChangeStatus = (status: string | undefined | null): boolean => {
  if (!status) return false;
  return !BLOCKED_STATUS_CHANGE.includes(status as CaseStatus);
};
